import { Box } from '@mui/material'
import React, { useEffect, useMemo, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui/ErrorFallback'
import useEnterPageLogAnalytics from '@/shared/hooks/useEnterPageLogAnalytics'
import ScrollToTopButton from '@/components/tv/components/ScrollToTopButton'
import useHomeContent from '@/pages/home/hooks/useHomeContent'
import { useVODStore } from '@/shared/store'
import { pageType } from '@/shared/store/types'
import { useAtmospheric } from '@/features/VOD/hooks/useAtmospheric'
import { VodRowViewProps } from '@/types/vod/vodRow'
import VodRowView from '@/features/VOD/components/VodRowView'
import { vodItemImageHeight } from '@/shared/constants'
import { Footer } from '@/features/Footer/Footer'
import { VodItemsProvider } from '@/features/VOD/context/UseGetVodItemContext/VodItemsContext'
import HomeContentSkeleton from '@/features/VOD/components/skeletons/HomeContentSckeleton'

const containerStyle = {
    height: '90vh',
    overflowY: 'auto',
    display: 'flex',
    gap: '50px',
    flexDirection: 'column',
    padding: '30px',
}

const HomeContent = () => {
    const homeContainerRef = useRef<HTMLDivElement | null>(null)

    const vodRowsRef = useRef<HTMLDivElement[]>([])
    const { homeRows, setHomeRows } = useHomeContent()
    const { setVodPages } = useVODStore()

    const scrollToTop = () => {
        const container = homeContainerRef.current
        if (container)
            container?.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
    }

    const handleGoToCategoryButtonClick = (
        itemId: string,
        title: string,
        type: pageType
    ): void => {
        setVodPages(itemId, title, type)
        homeContainerRef.current?.scrollTo(0, 0)
        setHomeRows([])
    }

    useEnterPageLogAnalytics('HOME')

    const { rowElements, handleScroll, handleRefsUpdate } = useAtmospheric(
        homeContainerRef,
        homeRows || []
    )

    useEffect(() => {
        handleRefsUpdate(vodRowsRef.current)
    }, [homeRows?.length, handleRefsUpdate, vodRowsRef.current])

    useEffect(() => {
        const container = homeContainerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [rowElements, homeContainerRef.current])

    const contentToDisplay = useMemo(() => {
        if (!homeRows?.length) return null

        return homeRows.map((row: VodRowViewProps, index: number) => {
            const {
                productId,
                title,
                hideCaptions,
                hideProgress,
                rowHeightPercentage,
                hideTitle,
            } = row

            return (
                <Box
                    key={productId}
                    data-index={row.productId}
                    ref={(listElement: HTMLDivElement) => {
                        vodRowsRef.current[index] = listElement
                    }}>
                    <VodItemsProvider productId={String(row.productId)}>
                        <VodRowView
                            productId={productId}
                            title={title}
                            hideTitle={hideTitle}
                            hideCaptions={hideCaptions}
                            hideProgress={hideProgress}
                            rowHeightPercentage={rowHeightPercentage}
                            maxHeight={vodItemImageHeight}
                            handleGoToCategoryButtonClick={
                                handleGoToCategoryButtonClick
                            }
                            isHomeContentRow={true}
                        />
                    </VodItemsProvider>
                </Box>
            )
        })
    }, [homeRows, handleGoToCategoryButtonClick])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box sx={containerStyle} id="here" ref={homeContainerRef}>
                {homeRows?.length ? (
                    contentToDisplay
                ) : (
                    <Box mt="60px">
                        <HomeContentSkeleton />
                    </Box>
                )}

                <Footer />
                <ScrollToTopButton
                    handleOnClick={scrollToTop}
                    container={homeContainerRef.current}
                />
            </Box>
        </ErrorBoundary>
    )
}

export default HomeContent
